import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Package, CheckCircle, MagnifyingGlass, ArrowCounterClockwise, PencilSimple, X } from '@phosphor-icons/react';
import ScannerScanner from '@/pages/scanner/ScannerScanner';
import { EngEquipmentFormDialog } from '@/pages/engineering/EngEquipmentFormDialog';
import { shizaiApi } from './shizaiApi';

const fmtQty = (n) => Number(n || 0).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });

/** 入荷スキャン — PIC scans/enters REQ番号 or Techs受注番号, confirms arrived
 *  quantities (+ optional actual price), requester gets notified. */
export default function PicReceiving() {
  const [req, setReq] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 技術資産へ自動登録しなかった明細（今回の入荷分）。トーストは消えるので
  // 画面にも残し、そこから設備台帳へ登録できるようにする。
  const [skipped, setSkipped] = useState([]);
  const [equipDialog, setEquipDialog] = useState(null); // { prefill, origin }
  const [openingEquip, setOpeningEquip] = useState(null);

  // 設備登録フォームの初期値は、開く瞬間に元の依頼から引き直す（中間レコードを
  // 作らない・入荷レコードを太らせない）。index → lines[index] で解決する。
  const openEquipRegister = async (s) => {
    setOpeningEquip(s.index);
    try {
      const fresh = await shizaiApi.picReceivingLookup(s.request_no);
      const ln = (fresh.lines || [])[s.index];
      if (!ln) { toast.error('元の依頼明細が見つかりませんでした'); return; }
      const qty = Number(ln.received_qty || ln.qty || 0);
      const amount = ln.received_amount != null
        ? Number(ln.received_amount)
        : (ln.unit_price != null ? Number(ln.unit_price) * qty : null);
      setEquipDialog({
        prefill: {
          name: ln.name || ln.part_no || '',
          maker: ln.maker || '',
          model_no: ln.model_no || ln.maker_pn || '',
          category: '',
          reference_url: ln.reference_url || '',
          acquisition_price: amount != null ? String(Math.round(amount)) : '',
          // 取得日は受領日を既定にする（編集可）。この明細の受領記録 `s.at` が
          // 受領時刻の正本。無ければ依頼の更新時刻に落とす。
          acquired_on: String(s.at || fresh.updated_at || '').slice(0, 10),
          description: [ln.drawing_no ? `図番: ${ln.drawing_no}` : '',
            `受領: ${fresh.request_no} 明細${s.index + 1}`,
            `数量: ${qty}${ln.uom || '個'}`,
            ln.notes || ''].filter(Boolean).join(' / '),
        },
        origin: { source_type: 'shizai_request', source_id: fresh.id,
          label: `${fresh.request_no}${ln.name ? ` / ${ln.name}` : ''}` },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '依頼の再取得に失敗しました');
    } finally {
      setOpeningEquip(null);
    }
  };

  const lookup = async (code) => {
    const v = (code || '').trim();
    if (!v || loading) return;
    setLoading(true);
    try {
      const d = await shizaiApi.picReceivingLookup(v);
      setReq(d);
      setRows((d.lines || []).map((ln, i) => {
        const remaining = Math.max(0, Number(ln.qty || 0) - Number(ln.received_qty || 0));
        return {
          index: i, name: ln.name, qty: Number(ln.qty || 0), uom: ln.uom || '個',
          received: Number(ln.received_qty || 0),
          received_amount: ln.received_amount != null ? Number(ln.received_amount) : null,
          in_qty: remaining > 0 ? String(remaining) : '',
          in_price: '',
          fixing: false, fix_qty: '', fix_amount: '',
        };
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '依頼が見つかりませんでした');
    } finally {
      setLoading(false);
    }
  };

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const fixingRows = rows.filter((r) => r.fixing);

  const submitFix = async () => {
    const lines = fixingRows.map((r) => ({
      index: r.index,
      received_qty: Number(r.fix_qty),
      received_amount: r.fix_amount !== '' ? Number(r.fix_amount) : null,
    }));
    if (lines.some((l) => !(l.received_qty >= 0))) { toast.error('修正後の入荷数は0以上の数値を入力してください'); return; }
    if (lines.some((l) => l.received_amount !== null && !(l.received_amount >= 0))) {
      toast.error('修正後の金額は0以上の数値を入力してください'); return;
    }
    setSaving(true);
    try {
      await shizaiApi.picReceiveFix(req.id, { lines });
      toast.success('入荷実績を修正しました');
      await lookup(req.request_no);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入荷実績の修正に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const lines = rows
      .filter((r) => Number(r.in_qty) > 0)
      .map((r) => ({
        index: r.index,
        qty: Number(r.in_qty),
        total_price: r.in_price !== '' ? Number(r.in_price) : null,
      }));
    if (lines.length === 0) { toast.error('今回入荷した数量を入力してください'); return; }
    const over = rows.some((r) => Number(r.in_qty) > Math.max(0, r.qty - r.received));
    if (over && !window.confirm('依頼数量を超える入荷数が入力されています。このまま登録しますか？')) return;
    if (lines.some((l) => l.total_price !== null && !(l.total_price >= 0))) {
      toast.error('金額は0以上の数値を入力してください'); return;
    }
    setSaving(true);
    try {
      const res = await shizaiApi.picReceive(req.id, { lines });
      toast.success(`入荷を登録しました（${res.status === 'received' ? '全て入荷済み' : '一部入荷'}）— 依頼者へ通知しました`);
      (res.asset_autoregister_skipped || []).forEach((s) => {
        toast.warning(`${s.name || s.part_no || '明細'}: 技術資産へ自動登録しませんでした`, {
          description: s.reason, duration: 12000,
        });
      });
      // §8.7 単品の物は 1台=1レコード。採れた番号をその場で見せる。
      const made = res.unit_records?.created || [];
      if (made.length) {
        toast.success(`${made[0].label}を ${made.length}件 登録しました`, {
          description: made.map((u) => u.no).join(' / '), duration: 14000,
        });
      }
      (res.unit_records?.warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      // 通知は残す（Round 3/4 の挙動は変えない）。加えて画面にも残して
      // 「ではどこに登録するのか」への入口を出す。
      setSkipped((res.asset_autoregister_skipped || []).map((s) => ({
        ...s, request_no: req.request_no, request_id: req.id,
      })));
      setReq(null);
      setRows([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入荷登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="pic-receiving">
      {skipped.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-2.5"
          data-testid="pic-receiving-skipped-panel">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-amber-900">
              技術資産へ自動登録しなかった明細（{skipped.length}件）
            </p>
            <button type="button" onClick={() => setSkipped([])}
              className="text-amber-700 hover:text-amber-900" title="閉じる"
              data-testid="pic-receiving-skipped-close">
              <X size={14} weight="bold" />
            </button>
          </div>
          {skipped.map((s) => (
            <div key={`${s.request_id}-${s.index}`}
              className="rounded-lg border border-amber-200 bg-white p-2.5"
              data-testid={`pic-receiving-skipped-${s.index}`}>
              <p className="text-[13px] font-medium text-zinc-900">{s.name || s.part_no || '明細'}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-zinc-600">{s.reason}</p>
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8"
                  onClick={() => openEquipRegister(s)}
                  disabled={openingEquip === s.index}
                  data-testid={`pic-receiving-register-equipment-${s.index}`}>
                  {openingEquip === s.index ? '読込中…' : '設備台帳に登録'}
                </Button>
                <span className="font-mono text-[10px] text-zinc-400">{s.request_no} 明細{s.index + 1}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!req && (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-3">
          <p className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
            <Package size={18} weight="duotone" className="text-emerald-600" />
            入荷スキャン — REQ番号 または Techs受注番号 を読み取り
          </p>
          <p className="text-xs text-zinc-500">
            ラベルのバーコードをスキャン、またはコードを手入力してください。
          </p>
          <ScannerScanner onDetected={lookup} disabled={loading} />
        </div>
      )}

      {req && (
        <div className="bg-white rounded-xl border border-emerald-200 shadow-sm p-4 space-y-4" data-testid="pic-receiving-result">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-mono text-sm font-bold text-zinc-900">{req.request_no}</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                依頼者: {req.requested_by || '—'} ・ Techs受注: {req.techs_order_no || '—'}
                {req.order_name ? ` ・ ${req.order_name}` : ''}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setReq(null); setRows([]); }} data-testid="pic-receiving-reset">
              <ArrowCounterClockwise size={14} className="mr-1" /> 別のコードを読む
            </Button>
          </div>

          <div className="space-y-2.5">
            {rows.map((r, i) => {
              const remaining = Math.max(0, r.qty - r.received);
              const done = remaining <= 0;
              return (
                <div key={i} className={`rounded-lg border p-3 ${done ? 'border-emerald-200 bg-emerald-50/40' : 'border-zinc-200'}`} data-testid={`recv-line-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium text-zinc-900 min-w-0">{r.name || '—'}</p>
                    <span className="flex items-center gap-1.5">
                      {done
                        ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] whitespace-nowrap"><CheckCircle size={10} className="mr-1" />入荷済</Badge>
                        : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] whitespace-nowrap">残 {fmtQty(remaining)} {r.uom}</Badge>}
                      {r.received > 0 && (
                        <button type="button"
                          onClick={() => setRow(i, r.fixing
                            ? { fixing: false }
                            : { fixing: true, fix_qty: String(r.received), fix_amount: r.received_amount != null ? String(r.received_amount) : '' })}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] font-semibold transition-colors ${
                            r.fixing ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                          data-testid={`recv-fix-toggle-${i}`}
                          title="登録済みの入荷実績を修正する">
                          {r.fixing ? <X size={11} weight="bold" /> : <PencilSimple size={11} />}
                          {r.fixing ? '修正をやめる' : '修正'}
                        </button>
                      )}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    依頼 {fmtQty(r.qty)} {r.uom} ・ 入荷済 {fmtQty(r.received)} {r.uom}
                    {r.received_amount != null && ` ・ 累計金額 ¥${fmtQty(r.received_amount)}`}
                  </p>
                  {r.fixing && (
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-2 grid grid-cols-2 gap-2" data-testid={`recv-fix-fields-${i}`}>
                      <div>
                        <p className="text-[10px] font-semibold text-amber-800 mb-1">修正後の入荷済数（累計・{r.uom}）</p>
                        <Input type="number" min="0" inputMode="decimal" value={r.fix_qty}
                          onChange={(e) => setRow(i, { fix_qty: e.target.value })}
                          className="h-9 bg-white" data-testid={`recv-fix-qty-${i}`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-amber-800 mb-1">修正後の累計金額（円・任意）</p>
                        <Input type="number" min="0" inputMode="decimal" value={r.fix_amount}
                          onChange={(e) => setRow(i, { fix_amount: e.target.value })}
                          placeholder="変更しない場合は空欄" className="h-9 bg-white" data-testid={`recv-fix-amount-${i}`} />
                      </div>
                    </div>
                  )}
                  {!done && !r.fixing && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] font-semibold text-zinc-500 mb-1">今回入荷数 ({r.uom})</p>
                        <Input type="number" min="0" inputMode="decimal" value={r.in_qty}
                          onChange={(e) => setRow(i, { in_qty: e.target.value })}
                          className="h-9" data-testid={`recv-qty-${i}`} />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-zinc-500 mb-1">合計金額（円・任意）</p>
                        <Input type="number" min="0" inputMode="decimal" value={r.in_price}
                          onChange={(e) => setRow(i, { in_price: e.target.value })}
                          placeholder="実費を入力" className="h-9" data-testid={`recv-price-${i}`} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {fixingRows.length > 0 ? (
            <Button onClick={submitFix} disabled={saving}
              className="w-full bg-teal-700 text-white font-semibold h-11"
              data-testid="pic-receiving-fix-submit">
              {saving ? '修正中...' : `入荷実績を修正する（${fixingRows.length}件）`}
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving}
              className="w-full bg-teal-700 text-white font-semibold h-11"
              data-testid="pic-receiving-submit">
              {saving ? '登録中...' : '入荷を登録して依頼者へ通知'}
            </Button>
          )}
          <p className="text-[11px] text-zinc-400 text-center">
            {fixingRows.length > 0
              ? '修正は累計値の上書きです。ステータス・単価・購入価格履歴も自動で再計算されます'
              : '金額を入力すると、その部品の単価（マスター原価）も自動更新されます'}
          </p>
        </div>
      )}

      {equipDialog && (
        <EngEquipmentFormDialog
          open
          onClose={() => setEquipDialog(null)}
          prefill={equipDialog.prefill}
          origin={equipDialog.origin}
          onSaved={() => {
            setEquipDialog(null);
            toast.success('設備台帳に登録しました');
          }}
        />
      )}
    </div>
  );
}
